<script lang="ts">
export default {
  name: 'UILayer',
};
</script>

<script setup lang="ts">
import { ref, computed, inject } from 'vue';
import ExportButton from './ExportButton.vue';
import ExportList from './ExportList.vue';
import { TowerInfo, UIColors } from '@/views/tower_visualizer/types/TowerVisualizerPage';
import { SaveTowerRequest } from '@/services/v2/TowerService';
import { towerService } from '@/services/v2/TowerService';
import { customEvents } from '@/store/CustomEvents';
import { useRouter } from 'vue-router';
import { ProgramTower } from '@/types/tower/v2/Tower';
import Zoom from '../tower/v2/Zoom.vue';
import { useStore } from 'vuex';
import { AppState } from '@/store';
import CanvasUtils from '@/utilities/tower/v2/Canvas';
import MultiTowerSaveModal from './MultiTowerSaveModal.vue';
import { TowerType } from '../tower_dashboard/types/tower-dashboard';
import { TowerCardData } from '../tower_dashboard/types/tower-dashboard';
import { ChevronsLeft } from 'lucide-vue-next';
import { IsUserAClient } from '@/utilities/AuthUtils';

defineProps<{
  uiColors: UIColors;
}>();

const showSaveError = ref(false);
const showCreateMultiTowerModal = ref(false);
const towerInfo = inject<TowerInfo>('towerInfo');
const router = useRouter();
const store = useStore<AppState>();

const activeTower = computed(() => {
  return towerInfo?.selectedTower.value;
});

const isNewTower = computed(() => {
  return !towerInfo?.tower.value.towerId;
});

const isUserAClient = computed(() => {
  return IsUserAClient(store.state.user);
});

async function handleMultiTowerSave(): Promise<void> {
  if (isNewTower.value) {
    showCreateMultiTowerModal.value = true;
    return;
  }

  if (!towerInfo?.towerChanged) return;
  towerInfo.canvas.value.setZoom(store?.state.towerCanvasState.zoomLevel ?? CanvasUtils.DEFAULT_ZOOM);

  try {
    const request = {
      tower: {
        ...towerInfo.tower.value,
        towerObject: JSON.stringify(towerInfo.canvas.value?.toJSON(['zoomLevel'])),
      } as ProgramTower,
    } as SaveTowerRequest;

    await towerService.saveTower(request);
    dispatchEvent(new CustomEvent(customEvents.towerVisualizerSetCanvasToCurrent));
  } catch (e) {
    console.error(e);
    showSaveError.value = true;

    setTimeout(() => {
      showSaveError.value = false;
    }, 3000);
  }
}

async function handleTowerModalSaveClick(versionLabel: string): Promise<void> {
  if (!towerInfo?.towerChanged) return;
  towerInfo.canvas.value.setZoom(store?.state.towerCanvasState.zoomLevel ?? CanvasUtils.DEFAULT_ZOOM);

  try {
    const request = {
      action: 'new',
      tower: {
        towerTypeId: TowerType.MultiTower,
        towerObject: JSON.stringify(towerInfo.canvas.value?.toJSON(['zoomLevel'])),
        versionLabel,
        accountId: towerInfo.accountId.value,
      } as ProgramTower,
    } as SaveTowerRequest;

    const savedTower = await towerService.saveTower(request);
    savedTower.userId = store.state.user?.user.id || 0;
    dispatchEvent(new CustomEvent(customEvents.towerVisualizerSetCanvasToCurrent));
    showCreateMultiTowerModal.value = false;

    towerInfo.tower.value = savedTower;

    router.replace(`${savedTower.towerId}`);
  } catch (e) {
    console.error(e);
    showSaveError.value = true;

    setTimeout(() => {
      showSaveError.value = false;
    }, 3000);
  }
}

function getTowerName(towerCard: TowerCardData): string {
  if (towerCard.towerTypeId === TowerType.MultiTower) {
    return 'Multi-Tower';
  } else if (towerCard.program) {
    return towerCard.program.name;
  } else if (towerCard.towerName) {
    return towerCard.towerName;
  }

  return '';
}
</script>

<template>
  <div
    class="layer-ui tw:opacity-95"
    :class="towerInfo?.towerChanged.value && !isUserAClient ? 'tw:border-4 tw:border-solid tw:border-red-500' : ''"
  >
    <div class="tw:pt-1 tw:flex tw:row-start-1 tw:col-start-1">
      <div
        @click="router.back()"
        class="tw:h-fit tw:bg-base-200 tw:border tw:border-solid tw:border-gray-200 tw:flex tw:items-center tw:justify-center tw:cursor-pointer tw:pointer-events-auto tw:border-box tw:rounded-md tw:shadow-lg tw:hover:bg-gray-300 tw:transition-all tw:px-1 tw:py-1 tw:m-2"
      >
        <ChevronsLeft :size="19" />
        <span class="tw:mr-2">Back</span>
      </div>
      <div v-if="activeTower" class="tw:flex tw:flex-col tw:pl-2 tw:h-fit tw:items-center">
        <span v-if="activeTower.versionLabel" class="tw:font-bold">{{ activeTower.versionLabel }}</span>
        <span class="tw:text-xs">{{ getTowerName(activeTower) }}</span>
      </div>
    </div>
    <button
      v-if="towerInfo?.towerChanged.value && !isUserAClient"
      class="tw:pointer-events-auto tw:absolute tw:btn tw:rounded-t-none tw:rounded-r-none tw:text-white tw:cursor-pointer tw:right-0 tw:p-1 tw:bg-red-500"
      @click="handleMultiTowerSave"
    >
      SAVE
    </button>
    <Zoom 
      class="tw:w-40 tw:pr-2 tw:col-start-1"
      bg-color="base-200"
      border-color="gray-300"
    />
    <div class="tw:col-start-2 tw:row-start-2 tw:flex tw:flex-col tw:w-full tw:h-full tw:justify-end">
      <ExportList class="tw:w-40 tw:pr-2 tw:self-end" />
      <ExportButton />
    </div>
    <div class="tw:toast" :class="{ 'tw:invisible': !showSaveError }">
      <div class="tw:alert tw:alert-error">
        <span>Error saving the multi-tower</span>
      </div>
    </div>
    <MultiTowerSaveModal
      :show="showCreateMultiTowerModal"
      @cancel="showCreateMultiTowerModal = false"
      @save="handleTowerModalSaveClick"
    />
  </div>
</template>

<style scoped>
.layer-ui {
  display: grid;
  justify-items: start;
  grid-template-columns: 1fr 1fr;
  grid-template-rows: 1fr 1fr;
  position: absolute;
  height: 100%;
  width: 100%;
  pointer-events: none;
  z-index: var(--zindex-layerui);
}
</style>
